import { defineStore } from 'pinia';

export const useWavoipStore = defineStore('wavoip', {
  state: () => ({
    isInitialized: false,
    isCallActive: false,
    isCallOutgoing: false,
    isCallIncoming: false,
    activeCall: null,
    incomingOffer: null,
    deviceTokens: [],
    deviceStatuses: {},
    webphoneAPI: null,
    callError: null,
  }),

  getters: {
    hasActiveCall: state => state.isCallActive,
    hasIncomingCall: state => state.isCallIncoming && state.incomingOffer !== null,
    isReady: state => state.isInitialized,
    getDeviceStatus: state => token => state.deviceStatuses[token] || 'unknown',
  },

  actions: {
    setWebphoneAPI(api) {
      this.webphoneAPI = api;
      this.isInitialized = true;
    },

    addDeviceToken(token) {
      if (!this.deviceTokens.includes(token)) {
        this.deviceTokens.push(token);
      }
    },

    removeDeviceToken(token) {
      this.deviceTokens = this.deviceTokens.filter(t => t !== token);
    },

    updateDeviceStatus(token, status) {
      this.deviceStatuses = {
        ...this.deviceStatuses,
        [token]: status,
      };
    },

    setIncomingOffer(offer) {
      this.incomingOffer = offer;
      this.isCallIncoming = true;
    },

    clearIncomingOffer() {
      this.incomingOffer = null;
      this.isCallIncoming = false;
    },

    setActiveCall(call) {
      this.activeCall = call;
      this.isCallActive = true;
      this.isCallIncoming = false;
      this.incomingOffer = null;
    },

    clearActiveCall() {
      this.activeCall = null;
      this.isCallActive = false;
      this.isCallOutgoing = false;
    },

    setCallOutgoing(val) {
      this.isCallOutgoing = val;
    },

    setCallError(error) {
      this.callError = error;
    },

    clearCallError() {
      this.callError = null;
    },

    async makeCall(phoneNumber, fromToken = null) {
      if (!this.webphoneAPI) {
        this.setCallError('WaVoIP not initialized');
        return;
      }

      this.clearCallError();
      this.setCallOutgoing(true);

      try {
        const config = {};
        if (fromToken) {
          config.fromTokens = [fromToken];
        }
        const result = await this.webphoneAPI.call.start(phoneNumber, config);

        if (result?.err) {
          this.setCallError(result.err.message || 'Call failed');
          this.setCallOutgoing(false);
          return;
        }
      } catch (error) {
        this.setCallError(error.message || 'Call failed');
        this.setCallOutgoing(false);
      }
    },

    async acceptCall() {
      // Accept is handled via the webphone UI itself
      // This action just updates state
      this.clearIncomingOffer();
    },

    // [FIX 2026-02-18] Actually reject the call via WaVoIP SDK
    // The webphone API exposes getOffers() which returns CallOffer objects
    // from @wavoip/wavoip-api — these have an accept() and reject() method
    // that sends the rejection signal to the WhatsApp device, ending the
    // call on the caller's side.
    async rejectCall() {
      if (this.webphoneAPI) {
        try {
          const offers = this.webphoneAPI.call.getOffers() || [];
          // Reject ALL pending offers (there's typically just one)
          const rejectPromises = offers
            .filter(offer => typeof offer.reject === 'function')
            .map(offer => offer.reject().catch(() => {}));
          await Promise.all(rejectPromises);

          // eslint-disable-next-line no-console
          console.log(
            `[WaVoIP] Rejected ${rejectPromises.length} call offer(s) via SDK`
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[WaVoIP] Error rejecting call via SDK:', err);
        }
      }
      this.clearIncomingOffer();
    },

    endCall() {
      this.clearActiveCall();
      this.clearIncomingOffer();
    },

    reset() {
      this.isInitialized = false;
      this.isCallActive = false;
      this.isCallOutgoing = false;
      this.isCallIncoming = false;
      this.activeCall = null;
      this.incomingOffer = null;
      this.webphoneAPI = null;
      this.callError = null;
    },
  },
});
